import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import api from "../../../services/api";

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    companyId: "",
    status: "ACTIVE",
  });

  useEffect(() => {
    fetchBrands();
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/api/system/brands");
      if (process.env.NODE_ENV === "development") {
        console.log("ブランド一覧取得レスポンス:", response.data);
      }
      setBrands(response.data || []);
    } catch (error) {
      console.error("ブランド一覧の取得に失敗しました:", error);
      setError("ブランド一覧の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get("/api/companies");
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("企業一覧の取得に失敗しました:", error);
    }
  };

  const handleOpenDialog = (brand = null) => {
    if (brand) {
      setEditingBrand(brand);
      setFormData({
        name: brand.name,
        companyId: brand.companyId || "",
        status: brand.status || "ACTIVE",
      });
    } else {
      setEditingBrand(null);
      setFormData({
        name: "",
        companyId: "",
        status: "ACTIVE",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBrand(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      if (editingBrand) {
        await api.put(`/api/brands/${editingBrand.id}`, formData);
      } else {
        await api.post("/api/brands", formData);
      }
      fetchBrands();
      handleCloseDialog();
    } catch (error) {
      console.error("ブランド情報の保存に失敗しました:", error);
      setError("ブランド情報の保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (brandId) => {
    if (window.confirm("このブランドを削除してもよろしいですか？")) {
      try {
        setDeletingId(brandId);
        await api.delete(`/api/brands/${brandId}`);
        fetchBrands();
      } catch (error) {
        console.error("ブランドの削除に失敗しました:", error);
        setError("ブランドの削除に失敗しました");
      } finally {
        setDeletingId(null);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "INACTIVE":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "ACTIVE":
        return "有効";
      case "INACTIVE":
        return "無効";
      default:
        return status;
    }
  };

  return (
    <Box aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1">
          ブランド管理
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          新規作成
        </Button>
      </Box>

      {!loading && brands.length === 0 ? (
        <Alert severity="info">ブランドが登録されていません</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ブランド名</TableCell>
                <TableCell>運営会社</TableCell>
                <TableCell align="center">店舗数</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>作成日</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : (
                brands.map((brand) => (
                  <TableRow key={brand.id}>
                    <TableCell>{brand.name}</TableCell>
                    <TableCell>{brand.companyName}</TableCell>
                    <TableCell align="center">{brand.storeCount}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(brand.status)}
                        color={getStatusColor(brand.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(brand.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(brand)}
                        disabled={Boolean(deletingId)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(brand.id)}
                        disabled={deletingId === brand.id}
                      >
                        {deletingId === brand.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBrand ? "ブランド情報編集" : "新規ブランド作成"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="ブランド名"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>運営会社</InputLabel>
                <Select
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleInputChange}
                  label="運営会社"
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id || company._id} value={company.id || company._id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>ステータス</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="ステータス"
                >
                  <MenuItem value="ACTIVE">有効</MenuItem>
                  <MenuItem value="INACTIVE">無効</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : "保存"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Brands;
